import {
  Modal,
  Form,
  Button,
  Alert,
  Logo,
} from '@insight-health-ai/roseate/components';
import { Select } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'src/hooks/auth';
import { analytics } from 'src/services';
import { getAppointmentDetailsForAnalytics } from 'src/utils';
type Props = {
  open?: boolean;
  onClose?: () => void;
};

const ConditionSelector = ({ open }: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const { appointment, getAppointmentConditions, updateAppointment } =
    useAuth();
  const [conditions, setConditions] = useState<
    Exclude<
      Awaited<ReturnType<typeof getAppointmentConditions>>,
      undefined | null
    >
  >([]);

  const selectOptions = useMemo(() => {
    const list: { label: string; value: number }[] = [];
    let currentCondition: { label: string; value: number } | undefined;
    conditions.forEach((condition) => {
      if (condition.condition_id === appointment?.condition?.id) {
        currentCondition = {
          label: condition.condition_name,
          value: condition.condition_id,
        };
      } else {
        list.push({
          label: condition.condition_name,
          value: condition.condition_id,
        });
      }
    });
    if (currentCondition) {
      list.push(currentCondition);
    }
    return list;
  }, [conditions]);

  const fetchGenericConditions = useCallback(async () => {
    setIsLoading(true);
    const conditions =
      (await getAppointmentConditions({
        appointmentId: appointment!.appointment_id,
      })) || [];

    setConditions(conditions || []);
    setIsLoading(false);
  }, [appointment?.appointment_id]);

  const onSubmit = useCallback(async (values: any) => {
    setIsLoading(true);
    const { condition } = values;
    analytics.track('Clicked Submit In Generic Conditions Modal', {
      ...getAppointmentDetailsForAnalytics(appointment!),
      condition_name: condition,
    });
    await updateAppointment({
      appointmentId: appointment!.appointment_id,
      conditionId: condition,
    });
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchGenericConditions();
  }, []);

  return (
    <Modal
      closable={false}
      open={!!open}
      title={
        <div className="flex flex-col items-center pb-[20px]">
          <Logo className="h-8" />
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <Alert
          appearance="success"
          message="Note"
          icon={<InfoCircleOutlined />}
          description={
            <>
              If you are unsure about your present health condition, choose{' '}
              <b>{appointment?.condition?.condition_name}.</b>
            </>
          }
        />
        <Form onFinish={onSubmit} size="lg">
          <Form.Item
            label="Select your health condition to continue"
            name="condition"
            rules={[
              {
                required: true,
                message: 'Please select your health condition',
              },
            ]}
          >
            <Select
              className="w-full"
              size="large"
              onChange={(value) => {
                analytics.track('Selected Condition In Generic Form', {
                  ...getAppointmentDetailsForAnalytics(appointment!),
                  condition: value,
                });
              }}
              options={selectOptions}
              placeholder="Select condition"
            />
          </Form.Item>
          <Form.Footer>
            <Button
              block
              size="lg"
              type="submit"
              loading={isLoading}
              disabled={isLoading}
            >
              Submit
            </Button>
          </Form.Footer>
        </Form>
      </div>
    </Modal>
  );
};

export default ConditionSelector;
